import * as React from 'react';
import { useEffect, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { CircularProgress, Collapse, IconButton, InputAdornment, TextField } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import NewReportIcon from 'assets/icons/ic-new-report.svg';
import { Spacing } from 'components/Layout';
import { fetchBetterviewWebToken } from 'services/submission';
import { updateBetterviewUrl } from 'services/location';
import { Location } from 'types/location.type';
import { WithStyles } from '@mui/styles';
import styles from '../styles';

interface BetterViewSectionProps {
    location: Partial<Location>;
    submissionId: string;
    classes: WithStyles<typeof styles>['classes'];
    onRefresh: () => void;
}

export const BetterViewSection: React.FC<BetterViewSectionProps> = ({ location, submissionId, classes, onRefresh }) => {
    const [betterviewJWT, setBetterviewJWT] = useState<string>('');
    const [showBetterviewUrlInput, setShowBetterviewUrlInput] = useState<boolean>(false);
    const [updateBetterviewUrlStr, setUpdateBetterviewUrlStr] = useState<string>(location.betterviewUrl || '');
    const [updateBetterviewUrlLoading, setUpdateBetterviewUrlLoading] = useState<boolean>(false);

    const interactiveBetterviewReportReady = !!betterviewJWT;
    const betterviewNonProductive = location.betterviewUrl === 'NON_PRODUCTIVE';

    const fetchBetterviewJWT = async () => {
        const tokenResponse = await fetchBetterviewWebToken();
        setBetterviewJWT(tokenResponse.token);
    };

    const sendUpdateBetterviewUrl = async (updatedUrl: string) => {
        setShowBetterviewUrlInput(false);
        setUpdateBetterviewUrlLoading(true);
        if (location?.id) {
            await updateBetterviewUrl(location.id, updatedUrl);
        }
        setUpdateBetterviewUrlLoading(false);
        onRefresh();
    };

    useEffect(() => {
        setBetterviewJWT('');
        if (location.betterviewUrl && !betterviewNonProductive) {
            fetchBetterviewJWT();
        }
    }, [location.betterviewUrl]);

    const renderBetterviewHeader = () => {
        return (
            <>
                BetterView&nbsp;
                {location.betterviewUrl ? (
                    <span style={{ display: 'inline-block' }}>Report</span>
                ) : (
                    <>
                        {betterviewNonProductive ? (
                            <span style={{ display: 'inline-block' }}>Report Non-Productive</span>
                        ) : (
                            !interactiveBetterviewReportReady && (
                                <span style={{ display: 'inline-block' }}>Report Pending</span>
                            )
                        )}
                    </>
                )}
                {updateBetterviewUrlLoading ? (
                    <CircularProgress style={{ display: 'inlineBlock' }} size={18} />
                ) : (
                    <EditIcon
                        style={{
                            width: '18px',
                            height: '18px',
                            margin: '5px',
                            verticalAlign: 'bottom',
                            cursor: 'hand',
                        }}
                        onClick={() => setShowBetterviewUrlInput(!showBetterviewUrlInput)}
                    />
                )}
                <Collapse style={{ display: 'inlineBlock' }} orientation="horizontal" in={showBetterviewUrlInput}>
                    <TextField
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        style={{ cursor: 'hand' }}
                                        onClick={() => sendUpdateBetterviewUrl(updateBetterviewUrlStr)}
                                        size="small"
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => {
                                            setUpdateBetterviewUrlStr('');
                                        }}
                                        size="small"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                        value={updateBetterviewUrlStr}
                        onChange={(event) => setUpdateBetterviewUrlStr(event.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                sendUpdateBetterviewUrl(updateBetterviewUrlStr);
                            }
                            e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                        }}
                        style={{ width: '525px' }}
                    />
                </Collapse>
            </>
        );
    };

    return (
        <Grid className={classes.margin} key={1}>
            <Typography className={classes.BVheaderLabel}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src={NewReportIcon}
                        alt="new report"
                        width="18px"
                        height="18px"
                        style={{ margin: '5px', verticalAlign: 'bottom' }}
                    />
                    {renderBetterviewHeader()}
                </div>
            </Typography>
            {interactiveBetterviewReportReady && (
                <>
                    <Spacing height={'8'} />
                    <div style={{ width: '90%', minWidth: 900, height: 800 }}>
                        <iframe
                            width={'100%'}
                            height={'100%'}
                            frameBorder="0"
                            title="location betterview report"
                            src={location.betterviewUrl + betterviewJWT}
                            allowFullScreen={true}
                        />
                    </div>
                </>
            )}
        </Grid>
    );
};
